import React from 'react';
import { PageHeader } from './PageHeader';
import { PageControls } from './PageControls';
import type { StatusCapsuleItem } from './StatusCapsule';

/**
 * 🏢 EnterprisePage — Standard page wrapper for all modules
 *
 * Enforces consistent layout: full-height flex column with
 * PageHeader + optional toolbar + scrollable content.
 * Horizontal padding provided by .enterprise-page CSS class.
 */

interface EnterprisePageProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  /** Status capsules rendered in the PageHeader strip */
  statusItems?: StatusCapsuleItem[];
  /** Right-aligned header actions */
  headerActions?: React.ReactNode;
  /** Optional toolbar content (filters, search, view toggles) rendered inside PageControls */
  toolbar?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export function EnterprisePage({
  title,
  subtitle,
  icon,
  statusItems,
  headerActions,
  toolbar,
  children,
  className = '',
}: EnterprisePageProps) {
  return (
    <div className={`enterprise-page flex h-full w-full flex-col ${className}`.trim()}>
      {/* Unified PageHeader — hidden when title is empty (StandardPageHeader used instead) */}
      {title ? (
        <PageHeader
          title={title}
          subtitle={subtitle}
          icon={icon}
          statusItems={statusItems}
          actions={headerActions}
        />
      ) : statusItems && statusItems.length > 0 ? (
        <PageHeader
          title=""
          statusItems={statusItems}
          actions={headerActions}
        />
      ) : null}

      {/* Optional toolbar (filters, search, view toggles) */}
      {toolbar && (
        <PageControls>{toolbar}</PageControls>
      )}

      {/* Scrollable content */}
      <div className="enterprise-page-content min-h-0 flex-1 overflow-auto">
        {children}
      </div>
    </div>
  );
}

export default EnterprisePage;
